import { verifyManifestHash, type MapManifest, type MapManifestEntry } from '@cars/shared';
import type { CarConfig, TrackConfig } from '../types';
import { TrackRuntime } from '../runtime/TrackRuntime';
import { ModelAsset } from './ModelAsset';
import {
  parseTrackVisualManifest,
  selectGroundVariant,
  type TrackVisualManifestV1,
} from './TrackVisualManifest';

/** Optional authored scene; presentation only, never read by physics. */
export interface TrackSceneAssets {
  manifest: TrackVisualManifestV1;
  ground: HTMLImageElement;
  model: ModelAsset;
}

export interface RaceAssets {
  track: TrackRuntime;
  landscape: HTMLImageElement;
  car: HTMLImageElement;
  scene: TrackSceneAssets | null;
}

export interface RaceLoadOptions {
  maxTextureSize: number;
}

/** Selected-track files only. API requests remain in api/client.ts. */
export class AssetLoader {
  async loadRace(
    trackConfig: TrackConfig,
    carConfig: CarConfig,
    signal: AbortSignal,
    options: RaceLoadOptions,
  ): Promise<RaceAssets> {
    const manifestLoad = this.loadManifest(signal);
    // Settles to null on any non-abort failure, so a broken scene never blocks racing.
    const sceneLoad = this.loadScene(trackConfig, manifestLoad, options.maxTextureSize, signal);
    try {
      const [manifest, bytes, landscape, car] = await Promise.all([
        manifestLoad,
        this.fetchFile(`/assets/maps/track-${trackConfig.id}.terrain.bin`, signal).then(
          (response) => response.arrayBuffer(),
        ),
        this.loadImage(trackConfig.landscapePath, signal),
        this.loadImage(carConfig.texturePath, signal),
      ]);
      signal.throwIfAborted();
      const track = new TrackRuntime(trackConfig);
      await track.initialize(bytes, manifest, {
        width: landscape.naturalWidth,
        height: landscape.naturalHeight,
      });
      const scene = await sceneLoad;
      if (signal.aborted) {
        scene?.model.dispose();
        signal.throwIfAborted();
      }
      return { track, landscape, car, scene };
    } catch (error) {
      void sceneLoad.then(
        (scene) => scene?.model.dispose(),
        () => undefined,
      );
      throw error;
    }
  }

  private async loadScene(
    trackConfig: TrackConfig,
    manifestLoad: Promise<MapManifest>,
    maxTextureSize: number,
    signal: AbortSignal,
  ): Promise<TrackSceneAssets | null> {
    const path = trackConfig.visualManifestPath;
    if (!path || import.meta.env.VITE_TRACK_VISUAL === 'rgb') return null;
    try {
      const base = new URL(path, window.location.href);
      const manifest = parseTrackVisualManifest(await (await this.fetchFile(path, signal)).json());
      const map = (await manifestLoad).maps[String(trackConfig.id)];
      if (
        manifest.trackId !== trackConfig.id ||
        !map ||
        manifest.referenceMapHash !== map.sha256 ||
        manifest.mapWidth !== map.width ||
        manifest.mapHeight !== map.height
      )
        throw new Error(`Scene for track ${trackConfig.id} was authored against a different map`);
      const variant = selectGroundVariant(manifest, maxTextureSize);
      if (!variant) throw new Error(`No scene ground image fits ${maxTextureSize}px textures`);
      const resolve = (file: string) => new URL(file, base).pathname;
      const [groundBlob, sceneryBytes] = await Promise.all([
        this.fetchVerified(resolve(variant.file), variant.sha256, signal).then(
          (bytes) => new Blob([bytes]),
        ),
        this.fetchVerified(resolve(manifest.scenery.file), manifest.scenery.sha256, signal),
      ]);
      const ground = await this.decodeImage(groundBlob, signal);
      const model = await ModelAsset.load(sceneryBytes.buffer, signal);
      return { manifest, ground, model };
    } catch (error) {
      if (signal.aborted) throw error;
      console.warn('Track scene unavailable, using landscape', error);
      return null;
    }
  }

  private async fetchVerified(
    path: string,
    sha256: string,
    signal: AbortSignal,
  ): Promise<Uint8Array<ArrayBuffer>> {
    const bytes = new Uint8Array(await (await this.fetchFile(path, signal)).arrayBuffer());
    await verifyManifestHash(bytes, sha256).catch(() => {
      throw new Error(`${path} does not match its scene manifest hash`);
    });
    return bytes;
  }

  private async fetchFile(path: string, signal: AbortSignal): Promise<Response> {
    const response = await fetch(path, { signal });
    if (!response.ok) throw new Error(`Unable to load ${path} (${response.status})`);
    return response;
  }

  private async loadImage(path: string, signal: AbortSignal): Promise<HTMLImageElement> {
    return this.decodeImage(await (await this.fetchFile(path, signal)).blob(), signal);
  }

  private async decodeImage(blob: Blob, signal: AbortSignal): Promise<HTMLImageElement> {
    signal.throwIfAborted();
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      signal.throwIfAborted();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private async loadManifest(signal: AbortSignal): Promise<MapManifest> {
    const value: unknown = await (
      await this.fetchFile('/assets/maps/manifest.json', signal)
    ).json();
    if (!isRecord(value) || value.formatVersion !== 2 || !isRecord(value.maps))
      throw new Error('Unsupported map manifest');
    const maps: Record<string, MapManifestEntry> = {};
    for (const [id, entry] of Object.entries(value.maps)) {
      if (
        !isRecord(entry) ||
        typeof entry.terrainFile !== 'string' ||
        typeof entry.sha256 !== 'string' ||
        !/^[a-f0-9]{64}$/.test(entry.sha256) ||
        typeof entry.width !== 'number' ||
        !Number.isInteger(entry.width) ||
        entry.width <= 0 ||
        typeof entry.height !== 'number' ||
        !Number.isInteger(entry.height) ||
        entry.height <= 0 ||
        entry.formatVersion !== 2 ||
        typeof entry.encodedBytes !== 'number'
      )
        throw new Error(`Invalid map manifest entry ${id}`);
      maps[id] = {
        terrainFile: entry.terrainFile,
        sha256: entry.sha256,
        width: entry.width,
        height: entry.height,
        formatVersion: 2,
        encodedBytes: entry.encodedBytes,
      };
    }
    return { formatVersion: 2, maps };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
